import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Users, Star, CheckCircle, Calendar, Phone, Mail } from "lucide-react";

const scheduleFlexanville = [
  { level: "Maternelles", time: "10h - 11h", day: "Mercredi matin" },
  { level: "CP / CE1", time: "11h - 12h", day: "Mercredi matin" },
  { level: "CE2 / CM1 / CM2", time: "12h - 13h", day: "Mercredi matin" },
];

const scheduleQueueMercredi = [
  { level: "Maternelle / CP", time: "10h - 11h", day: "Mercredi matin" },
  { level: "CE1 / CE2 / CM1 / CM2", time: "11h - 12h", day: "Mercredi matin" },
];

const scheduleQueueSamedi = [
  { level: "Maternelle / CP", time: "10h30 - 11h30", day: "Samedi matin" },
  { level: "CE1 / CE2 / CM1 / CM2", time: "11h30 - 12h30", day: "Samedi matin" },
];

const benefits = [
  { icon: Users, title: "Petits groupes", description: "Apprentissage personnalisé en effectif réduit" },
  { icon: Star, title: "Méthode ludique", description: "Jeux, chansons et activités adaptées à chaque âge" },
  { icon: CheckCircle, title: "Expression orale", description: "Focus sur la confiance et la prise de parole" },
];

export const EnglishClass = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-indigo-50">
      {/* Hero Section */}
      <section className="relative pt-28 pb-12 md:pb-16 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200 rounded-full blur-3xl opacity-40 hidden sm:block" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-indigo-200 rounded-full blur-3xl opacity-30 hidden sm:block" />
        
        <div className="container mx-auto px-4 md:px-6 max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <span className="text-xl">🇬🇧</span>
              <span>INSCRIPTIONS OUVERTES</span>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400"></span>
              </span>
            </div>

            <h1 className="text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl font-bold text-blue-900 mb-5 md:mb-6">
              <span className="text-gradient">English Class</span>
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-purple-700 font-medium mb-4">
              Apprendre l'anglais en s'amusant, dès la maternelle
            </p>
            
            <p className="text-base md:text-lg text-blue-800/70 max-w-2xl mx-auto mb-8">
              Des séances ludiques et adaptées à chaque âge, en petit groupe, 
              pour développer l'expression orale et la confiance en soi.
            </p>

            {/* Price Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl px-6 sm:px-8 py-4 shadow-xl mb-8"
            >
              <div className="text-center sm:text-left">
                <div className="text-sm font-medium opacity-90">Tarif annuel</div>
                <div className="text-3xl font-black">390€<span className="text-lg font-semibold">/an</span></div>
              </div>
              <div className="w-10 h-px sm:w-px sm:h-12 bg-white/30" />
              <div className="text-center sm:text-left">
                <div className="text-base sm:text-lg font-bold">1h de cours / semaine</div>
              </div>
            </motion.div>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                data-testid="english-class-contact-cta"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold shadow-lg transition-all hover:scale-105"
              >
                <span>Nous contacter</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="tel:+33664446823"
                data-testid="english-class-call-cta"
                className="sm:hidden inline-flex items-center justify-center gap-2 bg-white border-2 border-purple-200 text-purple-700 px-6 py-3.5 rounded-full text-base font-semibold shadow-sm transition-all"
              >
                <Phone className="w-5 h-5" />
                <span>06 64 44 68 23</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Age Groups */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Des cours adaptés à chaque âge
            </h2>
            <p className="text-blue-800/70 text-lg">
              3 groupes de niveaux pour un apprentissage optimal
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { age: "Maternelles", icon: "🌈", color: "from-pink-500 to-rose-500", desc: "Éveil à l'anglais par le jeu et les chansons" },
              { age: "CP / CE1", icon: "📚", color: "from-orange-500 to-amber-500", desc: "Premiers mots et expressions du quotidien" },
              { age: "CE2 / CM1 / CM2", icon: "🎯", color: "from-blue-500 to-indigo-500", desc: "Conversation et vocabulaire enrichi" },
            ].map((group, index) => (
              <motion.div
                key={group.age}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${group.color} rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity`} />
                <div className="relative bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-purple-200 transition-colors shadow-lg">
                  <div className="text-4xl mb-4">{group.icon}</div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{group.age}</h3>
                  <p className="text-blue-800/60">{group.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Notre méthode
            </h2>
            <p className="text-white/80 text-lg">
              Une approche ludique et bienveillante
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4">
                  <benefit.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-white/80">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Nos créneaux
            </h2>
            <p className="text-blue-800/70 text-lg">
              Choisissez le lieu et l'horaire qui vous convient
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Flexanville */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-6 md:p-8 border-2 border-orange-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-900">Flexanville</h3>
                  <p className="text-orange-600 font-medium">Mercredi matin</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {scheduleFlexanville.map((slot) => (
                  <div key={slot.level} className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-orange-500" />
                      <span className="font-semibold text-blue-900">{slot.level}</span>
                    </div>
                    <span className="text-orange-600 font-bold">{slot.time}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* La Queue-lez-Yvelines */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-6 md:p-8 border-2 border-purple-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-900">La Queue-lez-Yvelines</h3>
                  <p className="text-purple-600 font-medium">2 jours au choix</p>
                </div>
              </div>

              {/* Mercredi */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  <span className="font-semibold text-purple-700">Mercredi matin</span>
                </div>
                <div className="space-y-2">
                  {scheduleQueueMercredi.map((slot) => (
                    <div key={`mer-${slot.level}`} className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
                      <span className="font-medium text-blue-900">{slot.level}</span>
                      <span className="text-purple-600 font-bold">{slot.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Samedi */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  <span className="font-semibold text-purple-700">Samedi matin</span>
                </div>
                <div className="space-y-2">
                  {scheduleQueueSamedi.map((slot) => (
                    <div key={`sam-${slot.level}`} className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
                      <span className="font-medium text-blue-900">{slot.level}</span>
                      <span className="text-purple-600 font-bold">{slot.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à inscrire votre enfant ?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Contactez-nous dès maintenant pour réserver la place de votre enfant. 
              Les places sont limitées pour garantir la qualité de l'enseignement.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-purple-700 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition-all hover:scale-105"
              >
                <Mail className="w-5 h-5" />
                <span>Nous contacter</span>
              </a>
              <a
                href="tel:0664446823"
                className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition-all hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                <span>06 64 44 68 23</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            <span>Retour à l'accueil</span>
          </a>
        </div>
      </section>
    </div>
  );
};
